import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import I18n from 'i18n-js';

import { EUR_COUNTRIES } from '@lootboy/lib/payments';
import { displayNumber } from '@lootboy/lib/utils';
import { getDateFormatUnicode } from '@lootboy/lib/utils/dateAndTime';

import config from '~/config';
import {
  INTERNAL_PATH,
  LOOTBOY_COM_URL_BASE,
  LOOTBOY_DE_URL_BASE,
} from '~/constants/urls';
import { languageSelector } from '~/redux/selectors';

const CURRENCY_MAPPING = {
  BR: 'BRL',
  GB: 'GBP',
  US: 'USD',
};

const CURRENCY_UNIT = {
  USD: '$',
  BRL: 'R$',
  EUR: '€',
  GBP: '£',
  CHF: 'Fr.',
};

const EMAIL_REGEXP =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function getCurrencyForCountry(country) {
  if (EUR_COUNTRIES.includes(country)) {
    return 'EUR';
  }

  return CURRENCY_MAPPING[country] || 'USD';
}

export function validateEmail(email = '') {
  return EMAIL_REGEXP.test(email);
}

export function getCurrencyUnit(currency) {
  return CURRENCY_UNIT[currency];
}

export const getParsedNumber = (price, currency, customOptions = {}) => {
  const unit = getCurrencyUnit(currency) || '';
  let options = { unit };
  if (!unit) {
    options = {
      ...options,
      precision: 0,
      sign_first: false,
      ...customOptions,
    };
  }
  return I18n.toCurrency(price || 0, options).trim();
};

export const useNumberParser = () => {
  const language = useSelector(languageSelector);
  return useCallback(getParsedNumber, [language]);
};

export const useDateFormat = () => {
  const language = useSelector(languageSelector);
  return useMemo(() => getDateFormatUnicode(language), [language]);
};

/**
 * Formats a relative image `path` (in the format that CMS uses)
 * without altering the original image format provided in the `path`
 *
 * @param {string} path
 * @return {string}
 */
export const getImageUrl = (path) =>
  path && `${config.STATIC_RESOURCES_BASE_URL}/images/${path}`;
export const getVideoUrl = (path) =>
  path && `${config.STATIC_RESOURCES_BASE_URL}/videos/${path}`;

/**
 * Formats a relative image `path` (in the format that CMS uses)
 * to an absolute path to the image with a provided `imageFormat`.
 *
 * @param {string} path
 * @param {string} [imageFormat=".png"] image extension beginning with a dot character e.g. `.png`
 *
 * @return {string}
 */
export function getImageUrlWithFormat(path = '', imageFormat = '.png') {
  const fixedPath = path.replace(/\.png|\.jpeg|\.gif/, '');
  return `${config.STATIC_RESOURCES_BASE_URL}/images/${fixedPath}${imageFormat}`;
}

/**
 * @param uri {string}
 * @return {(string | null)}
 */
export const getPathFromInternalLink = (uri) => {
  if (typeof uri !== 'string') {
    return null;
  }
  if (uri.startsWith(INTERNAL_PATH)) {
    return uri.replace(INTERNAL_PATH, '');
  }
  if (uri.startsWith(LOOTBOY_DE_URL_BASE)) {
    return uri.replace(LOOTBOY_DE_URL_BASE, '');
  }
  if (uri.startsWith(LOOTBOY_COM_URL_BASE)) {
    return uri.replace(LOOTBOY_COM_URL_BASE, '');
  }
  if (uri.startsWith('/')) {
    return uri;
  }
  return null;
};

export const getRandomInt = (x, y) =>
  // returns random integer from range [x, y], x <= y
  x + Math.floor((y - x + 1) * Math.random());

export const getCountForBundle = (bundle) =>
  typeof bundle === 'string' ? Number(bundle.slice(1)) : 1;

export const convertToMap = (tabel, acumulator = {}) =>
  tabel.reduce((acc, value) => {
    acc[value._id || value.id] = value;
    return acc;
  }, acumulator);

export const preventEventPropagation = (event) => {
  event.preventDefault();
  event.stopPropagation();
  event.nativeEvent.stopImmediatePropagation();
};

/**
 *
 * @param timer {{ days: number, hours: number, minutes: number, seconds: number }}
 * @return {string}
 */
export function getTimerLabel(timer) {
  return `${timer.days ? `${displayNumber(timer.days)}:` : ''}${displayNumber(
    timer.hours,
  )}:${displayNumber(timer.minutes)}:${displayNumber(timer.seconds)}`;
}
